<svelte:options />

<script lang="ts">
  import { onMount } from 'svelte';
  import tippy, { type Instance as TippyInstance } from 'tippy.js'; // eslint-disable-line import/default, import/no-named-as-default, import/no-named-as-default-member -- Disable for compatibility
  import 'tippy.js/dist/tippy.css';
  import 'tippy.js/themes/light.css';

  import infoLightIcon from './images/info_light.svg';
  import { type Cursus, type CursusVak, type Periode } from './types/vakkenoverzicht';
  import { formatDateArray, getCursus } from './utils';

  interface Props {
    periode: Periode;
    vak: CursusVak;
    inschrijfUrl?: string;
  }
  const { periode, vak, inschrijfUrl = '' }: Props = $props();

  const cursusDagenOmschrijving = (cursus: Cursus) => {
    const dagen = cursus.dagen.filter((dag) => !cursus.nietopdagen.includes(dag));

    return formatDateArray(dagen);
  };
  const cursusOpmerkingen = (cursus: Cursus) => {
    const losseOpmerkingen: Array<string> = [];
    if (cursus.ondervoorbehoud) {
      losseOpmerkingen.push('Onder voorbehoud.');
    }

    if (cursus.nietopdagen.length > 0) {
      losseOpmerkingen.push('Let op: afwijkende data.');
    }

    if (cursus.opmerkingen) {
      losseOpmerkingen.push(cursus.opmerkingen);
    }

    return losseOpmerkingen.join('<br>');
  };

  const cursus = $derived(getCursus(periode, vak));
  const opmerkingen = $derived(cursus ? cursusOpmerkingen(cursus) : '');

  let tippyInstance: TippyInstance;
  let popoverElement = $state<HTMLAnchorElement>();
  let popoverContentElement = $state<HTMLTemplateElement>();

  const getTippyContent = () => popoverContentElement?.content.cloneNode(true) as DocumentFragment | undefined
    ?? window.document.createDocumentFragment();

  onMount(() => {
    tippyInstance = tippy(popoverElement!, { // eslint-disable-line @typescript-eslint/no-non-null-assertion -- This element should exist in template
      content: getTippyContent(),
      allowHTML: true,
      interactive: true,
    });
  });

  // Ensure Tippy content stays up-to-date
  $effect(() => {
    tippyInstance.setContent(getTippyContent());
  });
</script>

<!-- svelte-ignore a11y_missing_attribute -->
<a
  tabindex="0"
  class="popover-button"
  role="button"
  data-toggle="popover"
  bind:this={popoverElement}
>
  {#if opmerkingen}
    <img
      alt="info"
      class="info-icon"
      height="20"
      width="20"
      src={infoLightIcon}
    />
  {/if}
</a>
<template bind:this={popoverContentElement}>
  {#if cursus}
  <div class="cursus-gegevens">
    <b>Cursus:</b> {vak.naam} ({vak.niveau})<br>
    <b>Data:</b> {cursusDagenOmschrijving(cursus)}<br>
    {#if opmerkingen}
    <!-- eslint-disable-next-line svelte/no-at-html-tags -- Let's hope we can trust our colleagues -->
    <b>Opmerking(en):</b> {@html opmerkingen}<br>
    {/if}
    {#if inschrijfUrl}
    <div class="inschrijfknop-container">
      <a href={inschrijfUrl} target="_blank">
        <button class="btn btn-primary inschrijfknop">Inschrijven</button>
      </a>
    </div>
    {/if}
  </div>
  {/if}
</template>

<style lang="scss">
  :global(.tippy-content) {
    display: flex;
    flex-direction: column;
    text-align: left;
  }

  .popover-button {
    align-items: center;
    display: flex;
    height: 100%;
    justify-content: center;

    &:focus {
      border: 2px solid #000;
    }

    &:hover {
      background: rgb(255, 255, 255, 25%);
    }
  }

  // Overwrite height: auto from _page.scss
  .info-icon {
    height: 20px;
    width: 20px;
  }

  // Styles should be global, as Tippy will not render within Svelte scope
  :global(.inschrijfknop-container) {
    margin-top: 10px;
    text-align: right;
    width: 100%;
  }

  :global(.inschrijfknop) {
    padding: 3px 5px;
  }
</style>
